import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authService } from "../services/authServices";
import { toast } from "react-toastify";

const AuthContext = createContext({
  isPublicRoute: false,
  isAuthenticated: false,
  loading: true,
});

const publicPaths = [
  "/login",
  "/signup",
  "/forgot-password",
  "/otp-verification",
  "/reset-password",
  "/success",
  "/thank-you",
];

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  const isPublicRoute = publicPaths.includes(location.pathname);

  const handleInvalidToken = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("selectedMonth");
    localStorage.removeItem("mapStyle");
    setIsAuthenticated(false);
    setLoading(false);
    if (!isPublicRoute) {
      navigate("/login");
    }
  };

  const validateToken = async () => {
    if (!token) {
      handleInvalidToken();
      return;
    }

    try {
      const response = await authService.checkAuth(token);

      if (response.code === 200 || response.status === "ok") {
        setIsAuthenticated(true);

        // 🚫 Prevent access to public routes if logged in
        if (isPublicRoute) {
          navigate("/", { replace: true });
        }
      } else {
        handleInvalidToken();
        toast.error(response.message);
      }
    } catch (err) {
      const code = err?.response?.data?.code || err?.response?.status;
      const message = err?.response?.data?.message?.[0]?.detail;
      if (code === 400 || code === 401) {
        handleInvalidToken();
        if (code !== 401) toast.error(message);
      } else {
        toast.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    validateToken();
  }, [token, location.pathname]);

  // Periodic re-check
  useEffect(() => {
    if (!isPublicRoute) {
      const interval = setInterval(validateToken, 15 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isPublicRoute]);

  return (
    <AuthContext.Provider value={{ isPublicRoute, isAuthenticated, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
